import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Mail, Phone, MapPin, Send, Calendar, Globe, 
  Clock, Users, Building2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { submitLead } from '../services/api';
import { toast } from 'sonner';

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const propertyId = searchParams.get('property_id') || '';
  const partnerId = searchParams.get('partner_id') || '';
  const propertyTitle = searchParams.get('property_title') || '';
  const rawDestination = searchParams.get('destination') || '';

  const destinations = [
    'Greece', 'UAE (Dubai)', 'UK', 'Portugal', 'Egypt', 'USA', 'Canada', 'Singapore', 'Thailand', 'Multiple Countries'
  ];

  // Match raw country name to dropdown option (e.g. "UAE" -> "UAE (Dubai)")
  const prefilledDestination = rawDestination
    ? destinations.find(d => d.toLowerCase().startsWith(rawDestination.toLowerCase()) || d.toLowerCase().includes(rawDestination.toLowerCase())) || rawDestination
    : '';

  const [formData, setFormData] = useState({
    name: '',
    work_email: '',
    whatsapp: '',
    city: '',
    preferred_destination: prefilledDestination,
    investment_budget: '',
    timeline: '',
    purpose: propertyTitle ? `Inquiry about: ${propertyTitle}` : ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const budgetRanges = [
    'Under $500,000',
    '$500,000 - $1,000,000',
    '$1,000,000 - $2,500,000',
    '$2,500,000 - $5,000,000',
    '$5,000,000 - $10,000,000',
    'Over $10,000,000'
  ];

  const timelines = [
    'Immediate (Within 3 months)',
    'Short-term (3-6 months)',
    'Medium-term (6-12 months)',
    'Long-term (1-2 years)',
    'Just Exploring'
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await submitLead({
        ...formData,
        property_id: propertyId,
        partner_id: partnerId,
        property_title: propertyTitle
      });
      toast.success('Thank you for your interest! Our team will contact you within 24 hours.');
      setFormData({
        name: '',
        work_email: '',
        whatsapp: '',
        city: '',
        preferred_destination: '',
        investment_budget: '',
        timeline: '',
        purpose: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0C10] pt-20" data-testid="contact-page">
      {/* Hero */}
      <section className="py-16 border-b border-[#2A2F3A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="label-overline">Get in Touch</span>
            <h1 className="text-4xl sm:text-5xl text-white mt-4 mb-4">
              Begin Your Investment Journey
            </h1>
            <p className="text-[#A1A1AA] max-w-2xl mx-auto">
              Connect with our expert advisors for a personalized consultation. 
              We'll help you navigate the world of international property investment and residency.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-8"
              >
                <div>
                  <h3 className="text-xl text-white mb-6">Contact Information</h3>
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-5 w-5 text-[#D4AF37]" />
                      </div>
                      <div>
                        <h4 className="text-white font-medium mb-1">Office Address</h4>
                        <p className="text-[#A1A1AA] text-sm">
                          Suratwala Mark Plazzo,<br />
                          Hinjawadi Ph. 1, Pune
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                        <Phone className="h-5 w-5 text-[#D4AF37]" />
                      </div>
                      <div>
                        <h4 className="text-white font-medium mb-1">Phone</h4>
                        <p className="text-[#A1A1AA] text-sm">
                          <a href="tel:+918999570132" className="hover:text-white transition-colors">
                            +91 89995 70132
                          </a>
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                        <Mail className="h-5 w-5 text-[#D4AF37]" />
                      </div>
                      <div>
                        <h4 className="text-white font-medium mb-1">Email</h4>
                        <p className="text-[#A1A1AA] text-sm">
                          <a href="mailto:info@mulberryleaf.in" className="hover:text-white transition-colors">
                            info@mulberryleaf.in
                          </a>
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                        <Clock className="h-5 w-5 text-[#D4AF37]" />
                      </div>
                      <div>
                        <h4 className="text-white font-medium mb-1">Business Hours</h4>
                        <p className="text-[#A1A1AA] text-sm">
                          Mon - Fri: 9:00 AM - 6:00 PM IST<br />
                          Saturday: 10:00 AM - 2:00 PM IST
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-6">
                  <h4 className="text-white font-medium mb-6">Why Choose Us?</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <Globe className="h-6 w-6 text-[#D4AF37] mx-auto mb-2" />
                      <div className="text-xl text-white font-semibold">8</div>
                      <div className="text-[#A1A1AA] text-xs">Countries</div>
                    </div>
                    <div className="text-center">
                      <Building2 className="h-6 w-6 text-[#D4AF37] mx-auto mb-2" />
                      <div className="text-xl text-white font-semibold">500+</div>
                      <div className="text-[#A1A1AA] text-xs">Properties</div>
                    </div>
                    <div className="text-center">
                      <Users className="h-6 w-6 text-[#D4AF37] mx-auto mb-2" />
                      <div className="text-xl text-white font-semibold">1000+</div>
                      <div className="text-[#A1A1AA] text-xs">Happy Clients</div>
                    </div>
                    <div className="text-center">
                      <Calendar className="h-6 w-6 text-[#D4AF37] mx-auto mb-2" />
                      <div className="text-xl text-white font-semibold">5+</div>
                      <div className="text-[#A1A1AA] text-xs">Years Experience</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-8"
              >
                <h3 className="text-xl text-white mb-2">Schedule a Consultation</h3>
                <p className="text-[#A1A1AA] mb-8">
                  Fill out the form below and our investment advisors will get back to you within 24 hours.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6" data-testid="lead-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Name */}
                    <div>
                      <Label htmlFor="name" className="text-white mb-2 block">Full Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        placeholder="Enter your full name"
                        className="bg-[#0B0C10] border-[#2A2F3A] text-white placeholder:text-[#A1A1AA] focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                        required
                        data-testid="input-name"
                      />
                    </div>

                    {/* Work Email */}
                    <div>
                      <Label htmlFor="work_email" className="text-white mb-2 block">Work Email *</Label>
                      <Input
                        id="work_email"
                        type="email"
                        value={formData.work_email}
                        onChange={(e) => handleChange('work_email', e.target.value)}
                        placeholder="your@company.com"
                        className="bg-[#0B0C10] border-[#2A2F3A] text-white placeholder:text-[#A1A1AA] focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                        required
                        data-testid="input-email"
                      />
                    </div>

                    {/* WhatsApp */}
                    <div>
                      <Label htmlFor="whatsapp" className="text-white mb-2 block">WhatsApp Number *</Label>
                      <Input
                        id="whatsapp"
                        value={formData.whatsapp}
                        onChange={(e) => handleChange('whatsapp', e.target.value)}
                        placeholder="+91 XXXXX XXXXX"
                        className="bg-[#0B0C10] border-[#2A2F3A] text-white placeholder:text-[#A1A1AA] focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                        required
                        data-testid="input-whatsapp"
                      />
                    </div>

                    {/* City */}
                    <div>
                      <Label htmlFor="city" className="text-white mb-2 block">City *</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => handleChange('city', e.target.value)}
                        placeholder="Mumbai, Delhi, Bangalore..."
                        className="bg-[#0B0C10] border-[#2A2F3A] text-white placeholder:text-[#A1A1AA] focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                        required
                        data-testid="input-city"
                      />
                    </div>

                    {/* Preferred Destination */}
                    <div>
                      <Label className="text-white mb-2 block">Preferred Destination *</Label>
                      <Select 
                        value={formData.preferred_destination} 
                        onValueChange={(v) => handleChange('preferred_destination', v)}
                      >
                        <SelectTrigger 
                          className="bg-[#0B0C10] border-[#2A2F3A] text-white"
                          data-testid="select-destination"
                        >
                          <SelectValue placeholder="Select destination" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">
                          {destinations.map((dest) => (
                            <SelectItem key={dest} value={dest} className="text-white hover:bg-[#2A2F3A]">
                              {dest}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Investment Budget */}
                    <div>
                      <Label className="text-white mb-2 block">Investment Budget *</Label>
                      <Select 
                        value={formData.investment_budget} 
                        onValueChange={(v) => handleChange('investment_budget', v)}
                      >
                        <SelectTrigger 
                          className="bg-[#0B0C10] border-[#2A2F3A] text-white"
                          data-testid="select-budget"
                        >
                          <SelectValue placeholder="Select budget range" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">
                          {budgetRanges.map((budget) => (
                            <SelectItem key={budget} value={budget} className="text-white hover:bg-[#2A2F3A]">
                              {budget}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Timeline */}
                  <div>
                    <Label className="text-white mb-2 block">Investment Timeline *</Label>
                    <Select 
                      value={formData.timeline} 
                      onValueChange={(v) => handleChange('timeline', v)}
                    >
                      <SelectTrigger 
                        className="bg-[#0B0C10] border-[#2A2F3A] text-white"
                        data-testid="select-timeline"
                      >
                        <SelectValue placeholder="When are you looking to invest?" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">
                        {timelines.map((timeline) => (
                          <SelectItem key={timeline} value={timeline} className="text-white hover:bg-[#2A2F3A]">
                            {timeline}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Purpose / Remarks */}
                  <div>
                    <Label htmlFor="purpose" className="text-white mb-2 block">Purpose / Remarks (Optional)</Label>
                    <Textarea
                      id="purpose"
                      value={formData.purpose}
                      onChange={(e) => handleChange('purpose', e.target.value)}
                      placeholder="Tell us more about your investment goals, family requirements, or any specific questions..."
                      className="bg-[#0B0C10] border-[#2A2F3A] text-white placeholder:text-[#A1A1AA] focus:border-[#D4AF37] focus:ring-[#D4AF37] min-h-[120px]"
                      data-testid="input-purpose"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full btn-luxury py-6 text-base"
                    disabled={isSubmitting}
                    data-testid="submit-btn"
                  >
                    {isSubmitting ? (
                      'Submitting...'
                    ) : (
                      <>
                        Submit Enquiry <Send className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>

                  <p className="text-[#A1A1AA] text-xs text-center">
                    By submitting this form, you agree to our privacy policy and consent to be contacted by our team.
                  </p>
                </form>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
